import React from 'react'
import PropTypes from 'prop-types'
import { useSelector } from 'react-redux'
import { Typography, Divider, Stack, Box } from '@mui/material'
import { setDecimalPrecision } from 'utils/helpers'
import SimpleMaterialsTable from 'components/generic/printable/table/SimpleMaterialsTable'
import SignatureDateLine from 'components/generic/printable/components/SignatureDateLine'
import { formatAddress, convertShippingInfoToCityStateZip, dateFormatter } from 'utils/data-manipulation/valueFormatter'
import { TICKET_STATUSES } from 'utils/constants'

const ScaleReceiptForm = ({ inboundTicketData, notes, checkNumber, paymentConfirmed, pricePrepend, plateNumber }) => {
  const {
    ticketId,
    customer: customerInfo,
    carrierName,
    effectiveDate,
    transportationInfo,
    loadWeight,
    materials,
    netValue,
    roundingDelta,
    status,
    taxRate,
    freightCost,
    feeOrPromotion,
    feeOrPromotionTotal,
  } = inboundTicketData

  const { defaultYardUm, settings } = useSelector(state => state.settings)
  const { showWeightDecimals } = settings?.purchaseSettings?.inboundTickets || {}
  const { purchaseTicketDisclaimer } = settings?.documentSettings?.general || {}
  const customerName = customerInfo?.customerCommonIdentifierString
  const transportCarrierName = transportationInfo?.carrierName
  const carrierNumber = transportationInfo?.carrierNumber
  const containerNumber = transportationInfo?.containerNumber
  const grossWeight = loadWeight?.gross
  const tareWeight = loadWeight?.tare
  const netWeight = loadWeight?.net
  const date = new Date(effectiveDate)
  const formattedSubTotal = setDecimalPrecision(netValue, { shouldFormatNumber: false, decimalPrecisionOverride: 2 })
  const roundedSubTotal =
    parseFloat(formattedSubTotal) + parseFloat(roundingDelta || 0) + parseFloat(feeOrPromotionTotal || 0)
  const chargeback = freightCost?.chargeBack || 0

  return (
    <Box>
      <Stack>
        <Typography variant="h6" gutterBottom>
          {`Scale Ticket #${ticketId || ''} | ${
            paymentConfirmed
              ? 'PAID'
              : TICKET_STATUSES[status?.value?.toUpperCase() || TICKET_STATUSES.INCOMPLETE]?.name.toUpperCase()
          }`}
        </Typography>
        <Divider flexItem />
      </Stack>
      <Stack direction="row" mt={3}>
        <Box flex={3}>
          <Stack direction="row">
            <Typography sx={{ fontWeight: 'bold', width: 160 }}>Date:</Typography>
            <Typography>{dateFormatter(date)}</Typography>
          </Stack>
          <Stack direction="row">
            <Typography sx={{ fontWeight: 'bold', width: 160 }}>Customer Name:</Typography>
            <Typography>{customerName}</Typography>
          </Stack>
          <Stack direction="row">
            <Typography sx={{ fontWeight: 'bold', width: 160 }}>Carrier:</Typography>
            <Typography>{carrierName || transportCarrierName}</Typography>
          </Stack>
          <Stack direction="row">
            <Typography sx={{ fontWeight: 'bold', width: 160 }}>Carrier #:</Typography>
            <Typography>{carrierNumber}</Typography>
          </Stack>
          <Stack direction="row">
            <Typography sx={{ fontWeight: 'bold', width: 160 }}>Plate #:</Typography>
            <Typography>{plateNumber}</Typography>
          </Stack>
          <Stack direction="row">
            <Typography sx={{ fontWeight: 'bold', width: 160 }}>Container #:</Typography>
            <Typography>{containerNumber}</Typography>
          </Stack>
          {checkNumber && (
            <Stack direction="row">
              <Typography sx={{ fontWeight: 'bold', width: 160 }}>Check #:</Typography>
              <Typography>{checkNumber}</Typography>
            </Stack>
          )}
          <Stack direction="row">
            <Typography sx={{ fontWeight: 'bold', width: 160 }}>Location Address:</Typography>
            <Typography>
              {`${formatAddress(inboundTicketData?.address)}`}
              <br />
              {`${convertShippingInfoToCityStateZip(inboundTicketData?.address)}`}
            </Typography>
          </Stack>
        </Box>
        <Box flex={2}>
          <Stack direction="row" justifyContent="flex-end">
            <Typography>Gross Weight:</Typography>
            <Typography sx={{ width: 100, textAlign: 'right' }}>
              {`${setDecimalPrecision(grossWeight, {
                removeTrailingZeros: true,
                decimalPrecisionOverride: showWeightDecimals ? 1 : 0,
              })} ${defaultYardUm}`}
            </Typography>
          </Stack>
          <Stack direction="row" justifyContent="flex-end">
            <Typography>Tare Weight:</Typography>
            <Typography sx={{ width: 100, textAlign: 'right' }}>
              {`${setDecimalPrecision(tareWeight, {
                removeTrailingZeros: true,
                decimalPrecisionOverride: showWeightDecimals ? 1 : 0,
              })} ${defaultYardUm}`}
            </Typography>
          </Stack>
          <Stack direction="row" justifyContent="flex-end">
            <Typography>Net Weight:</Typography>
            <Typography sx={{ width: 100, textAlign: 'right' }}>
              {`${setDecimalPrecision(netWeight, {
                removeTrailingZeros: true,
                decimalPrecisionOverride: showWeightDecimals ? 1 : 0,
              })} ${defaultYardUm}`}
            </Typography>
          </Stack>
        </Box>
      </Stack>
      <Box mt={3}>
        <Typography sx={{ fontWeight: 'light' }} gutterBottom>
          NOTES
        </Typography>
        {notes?.length > 0 && (
          <>
            {notes?.map(note =>
              note?.isInternal ? (
                <></>
              ) : (
                <Typography variant="body2" gutterBottom>
                  {note?.note}
                </Typography>
              ),
            )}
          </>
        )}
      </Box>
      <Box mt={5}>
        <SimpleMaterialsTable
          materials={materials}
          taxRate={taxRate}
          subTotalOverride={roundedSubTotal}
          type="PRINT_PURCHASE_TICKET"
          isPrint
          chargeback={chargeback}
          pricePrepend={pricePrepend}
          feesAndPromotions={
            feeOrPromotion?.map(item => ({
              amount: item.amount,
              amountType: item.amountType,
              eligiblePaymentTypes: item.eligiblePaymentTypes,
              showPaymentMethodSpecificIcon:
                status?.value === TICKET_STATUSES.PAID.id ? false : item.eligiblePaymentTypes?.length < 4,
              id: item.settingId,
              name: item.name,
              netValue: item.calculatedValue,
              rowType: item.type.toLowerCase(),
            })) || []
          }
        />
      </Box>
      <Box mt={5}>
        <SignatureDateLine hideDate />
        <Typography variant="h6" gutterBottom>
          Disclaimer
        </Typography>
        <Typography variant="body1">{purchaseTicketDisclaimer || ''}</Typography>
      </Box>
    </Box>
  )
}

ScaleReceiptForm.propTypes = {
  inboundTicketData: PropTypes.instanceOf(Object),
  checkNumber: PropTypes.string,
  paymentConfirmed: PropTypes.bool,
  pricePrepend: PropTypes.string,
  plateNumber: PropTypes.string,
}

ScaleReceiptForm.defaultProps = {
  inboundTicketData: [],
  checkNumber: '',
  paymentConfirmed: false,
  pricePrepend: '$',
  plateNumber: '',
}

export default ScaleReceiptForm
